<script setup lang="ts">
import type { ImageRendererGestureState } from "~/classes/gestures/useImageRendererGestures"

import useImageRendererGestures from "~/classes/gestures/useImageRendererGestures"

const props = defineProps({
  file: null,
  gestureLayer: null,
  peek: { type: Boolean }
})
const { file, gestureLayer } = toRefs(props)
const { imageRendererContainerClass } = useGlobals()
const { mountGesture, unmountGesture } = useGestures()
const image = ref<HTMLImageElement>()
const container = ref<HTMLDivElement>()
const rendererState: Ref<ImageRendererGestureState> = ref({
  current: file.value,
  isDragging: false,
  isWheeling: false,
  viewX: 0,
  viewY: 0,
  viewScaleExp: 0,
  watchers: [],
})
const handler = useImageRendererGestures(container, image, gestureLayer.value, rendererState)

const containerStyleOverride = computed(() => {
  const style = { cursor: rendererState.value.isDragging ? "grabbing" : "grab" }

  if (!props.peek)
    return style

  const originalWidth = props.file.mediaWidthPx()
  const originalHeight = props.file.mediaHeightPx()
  if (!originalWidth || !originalHeight)
    return { ...style, width: "75vw", height: "75vh" }

  if (originalWidth > originalHeight)
    return { ...style, width: "75vw", height: `${75 * originalHeight / originalWidth}vw` }
  else
    return { ...style, width: `${75 * originalWidth / originalHeight}vh`, height: "75vh" }
})

onMounted(() => mountGesture(handler))
onUnmounted(() => unmountGesture(handler))
</script>

<template>
  <div
    ref="container"
    :class="{
      [imageRendererContainerClass]: true,
      peek,
    }"
    class="renderer-container"
    :style="containerStyleOverride"
  >
    <img
      ref="image"
      src=""
      class="renderer-image"
      draggable="false"
    >
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'
.renderer-container
  @apply h-full w-full overflow-hidden flex justify-center items-center
  @apply bg-[#{$page-peek-bg-item}]
.renderer-image
  @apply max-h-full max-w-full object-cover
</style>
